import { useEffect, useState } from "react";

import useDocumentTitle from "../../utils/useDocumentTitle";

import { useParams } from "react-router-dom";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";

import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";

import { useTranslation } from "react-i18next";
import styles from "./FAQ.module.scss";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";


export function FAQ(props) {
  const { t } = useTranslation();
  useDocumentTitle(props.title);
  const { id } = useParams();
  const { addNotification } = useNotification();
  const [faqList, setFaqList] = useState([]);

  const tableHeader = [
    { name: "id", label: "#", width: "10%" },
    { name: "issue", label: "Issue", width: "30%" },
    { name: "possible_reason", label: "Possible Reason", width: "20%" },
    { name: "solution", label: "Solution", width: "40%" },
  ];

  const beforeSessionIssueTableData = [
    {
      id: 1,
      issue: "Subunit not found.",
      possible_reason: "Subjects are not added, making the subunit inactive.",
      solution:
        "Functional Unit Manager to add subjects required under Subject Management. Users may seek assistance from Digital Lab Support (DLS) if required.",
    },
    {
      id: 2,
      issue: "Coach/Coachee account not found.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
    {
      id: 3,
      issue: "Subjects not listed.",
      possible_reason: "-",
      solution:
        "Functional Unit Manager to add subjects required; users to reach out to Functional Unit Manager first on missing subjects. Users may seek assistance from Digital Lab Support (DLS) if required.",
    },
    {
      id: 4,
      issue: "Subject duplicates found in the dropdown list.",
      possible_reason: "-",
      solution:
        "Functional Unit Manager to delete or modify subjects under Subject Management; users to reach out to Functional Unit Manager first on any duplicates. Users may seek assistance from Digital Lab Support (DLS) if required.",
    },
  ];

  const afterSessionIssueTableData = [
    {
      id: 1,
      issue: "“Start Coaching session” button disabled.",
      possible_reason:
        "<ol><li>Date of session has not arrived</li><li>Date of session has passed</li></ol>",
      solution:
        "<ol><li>Wait for session date to arrive</li><li>Coach and Coachee will have to restart entire coaching session</li></ol>",
      is_html: true,
    },
    {
      id: 2,
      issue: "Added subjects disappear.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
      is_html: false,
    },
    {
      id: 3,
      issue: "Unable to edit GROW form at coachee end.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
      is_html: false,
    },
    {
      id: 4,
      issue: "Unable to edit Start/Review dates from the coach end.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS)",
      is_html: false,
    },
    {
      id: 5,
      issue: "Unable to update Checkpoint scores.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
      is_html: false,
    },
    {
      id: 6,
      issue:
        "Session disappeared/not listed at either/both coach and coachee ends.",
      possible_reason:
        "Session was inactive (did not start session), marked as incomplete and archived.",
      solution: "Seek assistance from Digital Lab Support (DLS).",
      is_html: false,
    },
    {
      id: 7,
      issue: "Target Score shows “failed” though successful.",
      possible_reason:
        "Condition to Pass in Subject Management may be incorrect.",
      solution: "Functional Unit Manager to assist under Subject Management.",
      is_html: false,
    },
  ];

  const otherIssueTableData = [
    {
      id: 1,
      issue: "Unable to reset password.",
      possible_reason:
        "Incorrect username entered by user after Functional Unit Manager had reset user password.",
      solution:
        "Functional Unit Manager to assist in resetting password under User Management.",
    },
    {
      id: 2,
      issue: "OTP not received on primary email address.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
    {
      id: 3,
      issue: "Request for manual coach-coachee mapping.",
      possible_reason: "-",
      solution: "Functional Unit Manager to assist under User Management.",
    },
    {
      id: 4,
      issue: "Unable to find the coach/coachee account in User Management.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
    {
      id: 5,
      issue: "Request to add/update subjects under Subject Management.",
      possible_reason: "-",
      solution: "Functional Unit Manager to assist under Subject Management.",
    },
    {
      id: 6,
      issue:
        "Error: No campaign data found on coach/coachee account once logged in",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
    {
      id: 7,
      issue: "Unable to download report.",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
    {
      id: 8,
      issue: "No Data on downloaded report",
      possible_reason: "-",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
    {
      id: 9,
      issue: "Request for Reporting Access",
      possible_reason: "-",
      solution: "Seek assistance from Flash Coach team.",
    },
    {
      id: 10,
      issue: "Coach does not have access to create sessions",
      possible_reason:
        "Job Grade lower than 3 or no direct reporting line. Thus, default role is coachee.",
      solution: "Seek assistance from Digital Lab Support (DLS).",
    },
  ];

  async function fetchData(level_id) {
    const response = await apiRequest.get(
      `/api/getFAQInfo?level_id=${level_id}`
    );

    if (response.status === 200) {
      const data = response.data.data;
      //
      setFaqList(data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  useEffect(() => {
    const level_id = getLocalValue("u_levelid");
    fetchData(level_id);
  }, []);

  const helpAndSupportTable = (tableTitle, tableHeader, tableData) => {
    return (
      <TableContainer component={Paper} elevation={0}>
        <h5 className={styles.tableTitle}>
          {tableTitle.heading && <b>{tableTitle.heading}</b>}{" "}
          {tableTitle.text && tableTitle.text}
        </h5>
        <Table aria-label="Team" className={styles.table}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              {tableHeader &&
                tableHeader.map((tableHead, index) => (
                  <TableCell
                    key={index}
                    style={{ width: tableHead.width }}
                    className={styles.tableHeadCell}
                    onClick={() => { }}
                  >
                    {tableHead.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((item, index) => (
              <TableRow
                key={index}
                className={styles.tableRow}
                onClick={() => { }}
              >
                <TableCell component="th" scope="row">
                  {item.id}
                </TableCell>
                <TableCell>{item.issue}</TableCell>
                {item.is_html ? (
                  <TableCell
                    dangerouslySetInnerHTML={{ __html: item.possible_reason }}
                  />
                ) : (
                  <TableCell>{item.possible_reason}</TableCell>
                )}
                {item.is_html ? (
                  <TableCell
                    dangerouslySetInnerHTML={{ __html: item.solution }}
                  />
                ) : (
                  <TableCell>{item.solution}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <SectionTitle>{t("leftNavigation.helpAndSupport")}</SectionTitle>
            {/* <div className={styles.detailsContainer}>
              {faqList.map((faq) => (
                <Accordion
                  key={faq.id}
                  summary={<div className={styles.title}>{faq.question}</div>}
                  details={<div className={styles.content}>{faq.answer}</div>}
                />
              ))}
            </div> */}
            {/* <h5 className={styles.tableTitle}><b>Sessions</b>– Before Session, Invite Creation</h5> */}
            {helpAndSupportTable(
              {
                heading: "Sessions",
                text: " - Before Session, Invite Creation",
              },
              tableHeader,
              beforeSessionIssueTableData
            )}
            <p>
              <br />
            </p>
            {helpAndSupportTable(
              { heading: "Sessions", text: " - Post Session Creation" },
              tableHeader,
              afterSessionIssueTableData
            )}
            <p>
              <br />
            </p>
            {helpAndSupportTable(
              { heading: "Other Issues" },
              tableHeader,
              otherIssueTableData
            )}
            <p>
              <br />
            </p>
            <div>
              <p>
                <b>Digital Lab Support (DLS):</b>&nbsp;&nbsp;{" "}
                <a href="mailto:digital.support@tdcx.com">
                  digital.support@tdcx.com
                </a>
              </p>
              <p>
                <b>Flash Coach Team:</b>&nbsp;&nbsp;{" "}
                <a href="mailto:tdcxsg.flashcoach@tdcx.com">
                  tdcxsg.flashcoach@tdcx.com
                </a>
              </p>
            </div>
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
